import { supabase } from '../../../lib/supabaseClient';

// ==================== EVENTS ====================

export async function getSDEEvents() {
  return supabase
    .from('events')
    .select('*')
    .in('type', ['sde', 'sdo'])
    .order('event_start_date', { ascending: false });
}

export async function getSDEEvent(eventId) {
  return supabase
    .from('events')
    .select('*')
    .eq('id', eventId)
    .single();
}

export async function createSDEEvent(eventData) {
  return supabase
    .from('events')
    .insert([eventData])
    .select()
    .single();
}

export async function updateSDEEvent(eventId, updates) {
  return supabase
    .from('events')
    .update(updates)
    .eq('id', eventId)
    .select()
    .single();
}

// ==================== REGISTRATIONS ====================

export async function getRegistration(eventId, userEmail, populationType) {
  return supabase
    .from('sde_registrations')
    .select('*')
    .eq('event_id', eventId)
    .eq('user_email', userEmail)
    .eq('population_type', populationType)
    .maybeSingle();
}

export async function upsertRegistration(data) {
  return supabase
    .from('sde_registrations')
    .upsert(data, { onConflict: 'event_id,user_email,population_type' })
    .select()
    .single();
}

export async function getEventRegistrations(eventId, populationType) {
  let query = supabase
    .from('sde_registrations')
    .select('*')
    .eq('event_id', eventId);

  if (populationType) {
    query = query.eq('population_type', populationType);
  }

  return query.order('created_at', { ascending: false });
}

export async function getEventRegistrationsWithCandidatures(eventId) {
  return supabase
    .from('sde_registrations')
    .select(`
      *,
      candidatures_as_designer:sde_candidatures!sde_candidatures_designer_registration_id_fkey(*),
      candidatures_as_company:sde_candidatures!sde_candidatures_company_registration_id_fkey(*),
      meetings_as_designer:sde_meetings!sde_meetings_designer_registration_id_fkey(*),
      meetings_as_company:sde_meetings!sde_meetings_company_registration_id_fkey(*)
    `)
    .eq('event_id', eventId);
}

export async function getUserRegistrations(userEmail) {
  return supabase
    .from('sde_registrations')
    .select('*, event:event_id(id, title_fr, title_en, type, event_start_date)')
    .eq('user_email', userEmail)
    .order('created_at', { ascending: false });
}

export async function updateRegistrationStatus(registrationId, status) {
  return supabase
    .from('sde_registrations')
    .update({ status })
    .eq('id', registrationId)
    .select()
    .single();
}

export async function updateRegistrationAdminFields(registrationId, fields) {
  return supabase
    .from('sde_registrations')
    .update(fields)
    .eq('id', registrationId)
    .select()
    .single();
}

// ==================== CANDIDATURES ====================

export async function getCandidaturesForDesigner(eventId, designerRegistrationId) {
  return supabase
    .from('sde_candidatures')
    .select('*, company:company_registration_id(*)')
    .eq('event_id', eventId)
    .eq('designer_registration_id', designerRegistrationId)
    .order('priority', { ascending: true });
}

export async function getCandidaturesForCompany(eventId, companyRegistrationId) {
  return supabase
    .from('sde_candidatures')
    .select('*, designer:designer_registration_id(*)')
    .eq('event_id', eventId)
    .eq('company_registration_id', companyRegistrationId)
    .order('priority', { ascending: true });
}

// Supabase caps every response at 1000 rows server-side; page through to get the full set
async function fetchAllRows(buildQuery) {
  const rows = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await buildQuery().range(from, from + 999);
    if (error) return { data: null, error };
    rows.push(...data);
    if (data.length < 1000) return { data: rows, error: null };
  }
}

export async function getAllCandidatures(eventId) {
  return fetchAllRows(() => supabase
    .from('sde_candidatures')
    .select('*, designer:designer_registration_id(*), company:company_registration_id(*)')
    .eq('event_id', eventId)
    .order('id'));
}

export async function upsertCandidature(data) {
  return supabase
    .from('sde_candidatures')
    .upsert(data, {
      onConflict: 'event_id,designer_registration_id,company_registration_id'
    })
    .select()
    .single();
}

export async function updateCandidatureStatus(candidatureId, status) {
  return supabase
    .from('sde_candidatures')
    .update({ status })
    .eq('id', candidatureId)
    .select()
    .single();
}

export async function updateCandidaturePriority(candidatureId, priority) {
  return supabase
    .from('sde_candidatures')
    .update({ priority })
    .eq('id', candidatureId)
    .select()
    .single();
}

export async function deleteCandidature(candidatureId) {
  return supabase
    .from('sde_candidatures')
    .delete()
    .eq('id', candidatureId);
}

export async function bulkUpsertCandidatures(candidatures) {
  return supabase
    .from('sde_candidatures')
    .upsert(candidatures, {
      onConflict: 'event_id,designer_registration_id,company_registration_id'
    })
    .select();
}

// ==================== MEETINGS ====================

export async function getEventMeetings(eventId) {
  return supabase
    .from('sde_meetings')
    .select('*, company:company_registration_id(*), designer:designer_registration_id(*)')
    .eq('event_id', eventId);
}

export async function upsertMeeting(data) {
  return supabase
    .from('sde_meetings')
    .upsert(data)
    .select()
    .single();
}

export async function deleteMeetingsForPair(eventId, designerRegistrationId, companyRegistrationId) {
  return supabase
    .from('sde_meetings')
    .delete()
    .eq('event_id', eventId)
    .eq('designer_registration_id', designerRegistrationId)
    .eq('company_registration_id', companyRegistrationId);
}

export async function deleteMeeting(meetingId) {
  return supabase
    .from('sde_meetings')
    .delete()
    .eq('id', meetingId);
}

export async function deleteAllMeetingsForEvent(eventId) {
  return supabase
    .from('sde_meetings')
    .delete()
    .eq('event_id', eventId);
}

// ==================== FILE UPLOADS ====================

export async function uploadSDEFile(file, path) {
  const { data, error } = await supabase.storage
    .from('sde-files')
    .upload(path, file, {
      contentType: file.type || 'application/octet-stream',
      upsert: true,
    });

  if (error) return { data: null, error };

  const { data: urlData } = supabase.storage
    .from('sde-files')
    .getPublicUrl(data.path);

  return { data: { path: data.path, publicUrl: urlData.publicUrl }, error: null };
}

export async function deleteSDEFile(path) {
  return supabase.storage
    .from('sde-files')
    .remove([path]);
}

// ==================== STATS ====================

export async function getEventStats(eventId) {
  const [regs, cands, meetings] = await Promise.all([
    fetchAllRows(() => supabase.from('sde_registrations').select('id, population_type, status').eq('event_id', eventId).order('id')),
    fetchAllRows(() => supabase.from('sde_candidatures').select('id, status').eq('event_id', eventId).order('id')),
    fetchAllRows(() => supabase.from('sde_meetings').select('id').eq('event_id', eventId).order('id')),
  ]);

  const registrations = regs.data || [];
  const candidatures = cands.data || [];

  return {
    popA: {
      total: registrations.filter(r => r.population_type === 'pop_a').length,
      submitted: registrations.filter(r => r.population_type === 'pop_a' && r.status === 'submitted').length,
      validated: registrations.filter(r => r.population_type === 'pop_a' && r.status === 'validated').length,
    },
    popB: {
      total: registrations.filter(r => r.population_type === 'pop_b').length,
      submitted: registrations.filter(r => r.population_type === 'pop_b' && r.status === 'submitted').length,
      validated: registrations.filter(r => r.population_type === 'pop_b' && r.status === 'validated').length,
    },
    candidatures: {
      total: candidatures.length,
      confirmed: candidatures.filter(c => c.status === 'confirmed').length,
      pending: candidatures.filter(c => c.status === 'pending').length,
      rejected: candidatures.filter(c => c.status === 'rejected').length,
      waitlisted: candidatures.filter(c => c.status === 'waitlisted').length,
    },
    meetings: {
      total: meetings.data?.length || 0,
    }
  };
}
